import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArrowLeftOutlined, DoubleLeftOutlined } from '@ant-design/icons';
import throttle from 'lodash/throttle';
import { useHistory } from 'react-router-dom';

import style from './index.less';

interface StyleTypes {
  children?: JSX.Element | React.ReactNode | string;
  className?: string;
  style?: React.CSSProperties;
}

interface SettingLayoutTypes extends StyleTypes {
  row?: boolean;
}

interface HeaderTypes extends StyleTypes {
  back?: boolean;
  handleBack?: () => void;
  className?: string;
  contentClassName?: string;
}

interface ContentTypes extends StyleTypes {
  row?: boolean;
  maxWidth?: string;
  isNoPadding?: boolean;
}

interface GroupTypes extends ContentTypes {
  outerStyle?: React.CSSProperties;
}

interface ItemTypes extends StyleTypes {
  restProps?: {
    [K in string]: unknown;
  };
}

type RowTypes = StyleTypes;

interface SiderTypes {
  title: string;
  children?: React.ReactNode;
  className?: string;
  fold: boolean;
  onFold?: (val: boolean) => void;
  showHeader?: boolean;
  width?: number;
  foldWidth?: number;
  overflow?: boolean;
}

interface ISettingLayoutType extends React.FC<SettingLayoutTypes> {
  Header: React.FC<HeaderTypes>;
  Content: React.FC<ContentTypes>;
  Group: React.FC<GroupTypes>;
  Item: React.FC<ItemTypes>;
  Row: React.ForwardRefExoticComponent<RowTypes & React.RefAttributes<HTMLDivElement>>;
  Sider: React.FC<SiderTypes>;
  NewContent: React.FC<ContentTypes>;
  BlackListContent: React.FC<ContentTypes>;
}

const SettingLayout: ISettingLayoutType = (props: SettingLayoutTypes) => {
  const { children, className = '', style: propStyle, row } = props;
  const flexDirection = !row ? 'column' : 'row';
  return (
    <div className={`${style.main} ${className}`} style={{ ...propStyle, flexDirection }}>
      {children}
    </div>
  );
};

const Header: React.FC<HeaderTypes> = (props: HeaderTypes) => {
  const { children, className, style: propStyle, back, handleBack, contentClassName } = props;
  const history = useHistory();

  const handleBackClick = () => {
    if (typeof handleBack === 'function') {
      handleBack();
    } else {
      history.goBack();
    }
  };

  const renderBackButton = () => {
    if (back) {
      return (
        <div className={style.back} onClick={handleBackClick}>
          <ArrowLeftOutlined />
        </div>
      );
    }
    return null;
  };

  return (
    <header className={`${style.header} ${className}`} style={propStyle}>
      {renderBackButton()}
      <div className={contentClassName}>{children}</div>
    </header>
  );
};

const Content: React.FC<ContentTypes> = (props: ContentTypes) => {
  const { children, className, maxWidth, row, style: propsStyle } = props;
  const flexDirection = !row ? 'column' : 'row';
  return (
    <section className={`${style.outer}`}>
      <div className={`${style.content}`}>
        <div className={`${style.inner} ${className}`} style={{ ...propsStyle, flexDirection, maxWidth }}>
          {children}
        </div>
      </div>
    </section>
  );
};

const NewContent: React.FC<ContentTypes> = (props: ContentTypes) => {
  const { children, className, maxWidth, row, style: propsStyle } = props;
  const flexDirection = !row ? 'column' : 'row';
  return (
    <section className={`${style.newOuter}`}>
      <div className={`${style.newContent}`}>
        <div className={`${style.newInner} ${className}`} style={{ ...propsStyle, flexDirection, maxWidth }}>
          {children}
        </div>
      </div>
    </section>
  );
};

const BlackListContent: React.FC<ContentTypes> = (props: ContentTypes) => {
  const { children, className, maxWidth, row, style: propsStyle } = props;
  const flexDirection = !row ? 'column' : 'row';
  return (
    <section className={`${style.newOuter}`}>
      <div className={`${style.blackListContent}`}>
        <div className={`${style.newInner} ${className}`} style={{ ...propsStyle, flexDirection, maxWidth }}>
          {children}
        </div>
      </div>
    </section>
  );
};

const Group: React.FC<GroupTypes> = (props: GroupTypes) => {
  const { children, className, style: propsStyle, row, maxWidth, outerStyle, isNoPadding } = props;
  const padding = isNoPadding ? '0' : !row ? '16px 16px 0 16px' : '16px 0 16px 16px';
  const flexDirection = !row ? 'column' : 'row';
  return (
    <section className={style.outer} style={{ ...outerStyle }}>
      <div className={`${style.group} ${className}`} style={{ maxWidth, flexDirection, padding, ...propsStyle }}>
        {children}
      </div>
    </section>
  );
};

const Item: React.FC<ItemTypes> = (props: ItemTypes) => {
  const { children, className: classname, style: propsStyle, restProps } = props;

  return (
    <div className={`${style.item} ${classname}`} style={{ ...propsStyle }} {...restProps}>
      {children}
    </div>
  );
};

const Row = React.forwardRef<HTMLDivElement, RowTypes>((props, ref) => {
  const { children, className: classname, style: propsStyle } = props;

  return (
    <div ref={ref} className={`${style.row} ${classname}`} style={{ ...propsStyle }}>
      {children}
    </div>
  );
});

const Sider: React.FC<SiderTypes> = props => {
  const { title = '', children = null, onFold = () => null, fold = false, showHeader = true, className = '', width = 325, foldWidth = 60, overflow = true } = props;
  const siderRef = useRef(null);
  const [hideLeft, setHideLeft] = useState(false);

  const hideClick = useCallback(() => {
    setHideLeft(!hideLeft);
    onFold && onFold(!hideLeft);
  }, [hideLeft, onFold]);

  useEffect(() => {
    setHideLeft(fold);
  }, [fold]);

  // 兼容性处理 100% 高度有可能无效
  const getHeight = useCallback(() => {
    if (siderRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const height = siderRef.current.parentElement.clientHeight;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      siderRef.current.style.height = `${height}px`;
    }
  }, []);

  const throttleResize = useMemo(() => {
    return throttle(getHeight, 1000);
  }, [getHeight]);

  const siderWidth = !hideLeft
    ? { flex: `0 0 ${width}px`, width: `${width}px` }
    : { flex: `0 0 ${foldWidth}px`, width: `${width}px` };

  useEffect(() => {
    getHeight();

    window.addEventListener('resize', throttleResize);

    return () => {
      window.removeEventListener('resize', throttleResize);
    };
  }, []);

  return (
    <div ref={siderRef} style={{ ...siderWidth }} className={`${style.sider} ${className}`}>
      {showHeader ? (
        <ul className={style.sider_header}>
          {!hideLeft ? <li className={style.sider_title}>{title}</li> : null}
          <li>
            <a className={style.btn} onClick={hideClick} role='button'>
              <DoubleLeftOutlined className={`${style.arrow} ${hideLeft ? style.arrow__hidden : ''}`} />
            </a>
          </li>
        </ul>
      ) : null}
      <div className={`${style.sider_content} ${hideLeft ? 'hidden' : ''} ${overflow ? style.overflow : ''}`}>
        {children}
      </div>
    </div>
  );
};

SettingLayout.Header = Header;
SettingLayout.Content = Content;
SettingLayout.Group = Group;
SettingLayout.Item = Item;
SettingLayout.Row = Row;
SettingLayout.Sider = Sider;
SettingLayout.NewContent = NewContent;
SettingLayout.BlackListContent = BlackListContent;

export default SettingLayout;
